'use client';
import { createContext, useState, type Dispatch, type ReactNode, type SetStateAction } from 'react';

import type { ICart } from '@/interfaces/cart.interface';
import type { IMedia } from '@/interfaces/homeBanner.interface';
import type { IOrder } from '@/interfaces/order.interface';
import type { IProduct } from '@/interfaces/products.interface';
import type { IFashionStory } from '@/interfaces/story.interface';
import type { IUser } from '@/interfaces/user.interface';
import type { IWishlist } from '@/interfaces/wishlist.interface';

type TMenu = never[];

export type TStoreContextValue = {
  featureStoryState: [IFashionStory[], Dispatch<SetStateAction<IFashionStory[]>>];
  orderState: [IOrder[], Dispatch<SetStateAction<IOrder[]>>];
  wishlistState: [IWishlist[], Dispatch<SetStateAction<IWishlist[]>>];
  menuState: [TMenu, Dispatch<SetStateAction<TMenu>>];
  cartState: [ICart | null, Dispatch<SetStateAction<ICart | null>>];
  profileState: [IUser | null, Dispatch<SetStateAction<IUser | null>>];
  homeBannerState: [IMedia | null, Dispatch<SetStateAction<IMedia | null>>];
  productState: [IProduct[], Dispatch<SetStateAction<IProduct[]>>];
  homeProductState: [IProduct[], Dispatch<SetStateAction<IProduct[]>>];
  loginModeState: [boolean, Dispatch<SetStateAction<boolean>>];
  registerModalState: [boolean, Dispatch<SetStateAction<boolean>>];
};

// Create the context with default fallback
export const StoreContext = createContext<TStoreContextValue>({
  featureStoryState: [[], () => {}],
  orderState: [[], () => {}],
  wishlistState: [[], () => {}],
  menuState: [[], () => {}],
  cartState: [null, () => {}],
  profileState: [null, () => {}],
  homeBannerState: [null, () => {}],
  productState: [[], () => {}],
  homeProductState: [[], () => {}],
  loginModeState: [false, () => {}],
  registerModalState: [false, () => {}],
});

const StoreComponent = ({ children }: { children: ReactNode }) => {
  const [homeProducts, setHomeProducts] = useState<IProduct[]>([]);
  const [products, setProducts] = useState<IProduct[]>([]);
  const [menus, setMenus] = useState<TMenu>([]);
  const [featureStories, setFeatureStories] = useState<IFashionStory[]>([]);
  const [carts, setCarts] = useState<ICart | null>(null);
  const [wishlist, setWishlist] = useState<IWishlist[]>([]);
  const [profile, setProfile] = useState<IUser | null>(null);
  const [homeBanner, setHomeBanner] = useState<IMedia | null>(null);
  const [orders, setOrders] = useState<IOrder[]>([]);
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState<boolean>(false);
  const [isLoginMode, setIsLoginMode] = useState<boolean>(false);

  return (
    <StoreContext.Provider
      value={{
        featureStoryState: [featureStories, setFeatureStories],
        orderState: [orders, setOrders],
        wishlistState: [wishlist, setWishlist],
        menuState: [menus, setMenus],
        cartState: [carts, setCarts],
        profileState: [profile, setProfile],
        homeBannerState: [homeBanner, setHomeBanner],
        productState: [products, setProducts],
        homeProductState: [homeProducts, setHomeProducts],
        loginModeState: [isLoginMode, setIsLoginMode],
        registerModalState: [isRegisterModalOpen, setIsRegisterModalOpen],
      }}
    >
      {children}
    </StoreContext.Provider>
  );
};

export default StoreComponent;
